import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Factory, Cog, ShieldCheck, Truck, Users, Award, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCapabilities = [
  { icon: "Factory", title: "Efficient Project Planning", description: "End-to-end project planning from design to handover — covering material sourcing, production scheduling, and milestone tracking for seamless execution." },
  { icon: "Cog", title: "Swift Multi-City Project Handling", description: "Simultaneous execution across multiple cities with centralized project coordination, ensuring consistent quality and timelines at every location." },
  { icon: "ShieldCheck", title: "Precision Time Management", description: "Strict adherence to committed timelines through systematic production planning, real-time progress tracking, and proactive bottleneck resolution." },
  { icon: "Truck", title: "Coordinated Deliveries", description: "Synchronized dispatch and logistics management across India — ensuring the right products reach the right site at the right time, every time." },
  { icon: "Users", title: "Collaborative On-Site Installations", description: "Professional installation teams that coordinate seamlessly with other on-site contractors, architects, and facility managers for zero-disruption setup." },
  { icon: "Award", title: "Scalable Production Capacity", description: "Flexible manufacturing capability to handle projects of every scale — from compact 50-seater offices to large corporate campuses — without compromising quality or speed." },
];

const defaultProcessSteps = [
  { number: "01", title: "Material Sourcing", description: "Premium raw materials from certified suppliers" },
  { number: "02", title: "Precision Cutting", description: "CNC-controlled cutting for perfect dimensions" },
  { number: "03", title: "Edge Processing", description: "Seamless edge banding for durability" },
  { number: "04", title: "Assembly", description: "Expert craftsman assembly and fitting" },
  { number: "05", title: "Quality Check", description: "Multi-point inspection before dispatch" },
  { number: "06", title: "Packaging", description: "Secure packaging for safe transportation" },
];

const defaultContent = {
  heroTitle: "Manufacturing & Infrastructure",
  heroSubtitle: "Infrastructure",
  heroDescription: "State-of-the-art manufacturing facility",
  heroImage: "https://images.unsplash.com/photo-1720036236694-d0a231c52563?auto=format&fit=crop&w=1280&q=70",
  capabilities: defaultCapabilities,
  processSteps: defaultProcessSteps
};

const iconMap = { Factory, Cog, ShieldCheck, Truck, Users, Award };

const FACTORY_VIDEO_URL = "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/n2rau2m5_The%20Machine%20Heart.mp4";
const FACTORY_THUMBNAIL = "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8399y89k_Gemini_Generated_Image_eenxp0eenxp0eenx.png";

function FactoryVideoPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => {
      if (videoRef.current) videoRef.current.play();
    }, 100);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="relative max-w-5xl mx-auto"
      data-testid="factory-video-player"
    >
      <div className="relative aspect-video overflow-hidden border border-white/10">
        {!isPlaying && (
          <>
            <img src={FACTORY_THUMBNAIL}
              alt="Ezeefit Office Systems factory tour - Modular office furniture manufacturing facility in Bhiwandi, Thane"
              className="w-full h-full object-cover cursor-pointer"
              onClick={handlePlay} loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={handlePlay}>
              <div className="w-20 h-20 md:w-24 md:h-24 bg-[#F97316] flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-2xl shadow-orange-500/30">
                <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 pointer-events-none">
              <h3 className="text-white text-lg md:text-2xl font-bold mb-1">
                Take a Virtual Tour of Our Manufacturing Process
              </h3>
              <p className="text-white/70 text-sm md:text-base">
                A decade of manufacturing excellence. Thousands of workstations. One standard of precision.
              </p>
            </div>
          </>
        )}
        {isPlaying && (
          <video
            ref={videoRef}
            src={FACTORY_VIDEO_URL}
            className="w-full h-full object-contain bg-black"
            autoPlay
            controls
            playsInline
          />
        )}
      </div>
    </motion.div>
  );
}

export default function ManufacturingPage() {
  const [content, setContent] = useState(defaultContent);

  useCanonical("https://ezeefitofficesystems.in/manufacturing");
  useMetaDescription("Inside Ezeefit's office furniture factory in Bhiwandi, Thane — automated CNC borers, edge banding, panel saws. ISO 9001:2015. Factory-direct since 2014.");

  useEffect(() => {
    document.title = 'Office Furniture Factory in Bhiwandi, Thane | Manufacturing | Ezeefit Office Systems';
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/manufacturing`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(prev => ({
            ...prev,
            ...response.data,
            capabilities: (response.data.capabilities || []).length > 0
              ? response.data.capabilities.map(c => typeof c === 'object' ? c : { title: c, description: "" })
              : prev.capabilities,
            processSteps: (response.data.processSteps || []).length > 0
              ? response.data.processSteps
              : prev.processSteps
          }));
        }
      } catch (error) {
        console.log("Using default manufacturing content");
      }
    };
    fetchContent();
  }, []);

  const getIcon = (iconName, index) => {
    return iconMap[iconName] || [Factory, Cog, ShieldCheck, Truck, Users, Award][index % 6];
  };

  const capabilities = content.capabilities || defaultCapabilities;
  const processSteps = content.processSteps || defaultProcessSteps;

  return (
    <div className="pt-20" data-testid="manufacturing-page">
      <Helmet>
        <title>Office Furniture Factory in Bhiwandi, Thane | Manufacturing | Ezeefit Office Systems</title>
        <meta name="description" content="Inside Ezeefit's office furniture factory in Bhiwandi, Thane — automated CNC borers, edge banding, panel saws. ISO 9001:2015. Factory-direct since 2014." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/manufacturing" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({"@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"}, {"@type": "ListItem", "position": 2, "name": "Manufacturing", "item": "https://ezeefitofficesystems.in/manufacturing"}]}) }} />
      {/* Hero */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={content.heroImage}
            alt="Ezeefit modular office furniture manufacturing facility with automated CNC machinery in Bhiwandi, Thane"
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              {content.heroSubtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {content.heroTitle}
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mb-8">
              {content.heroDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Us - Brand Introduction */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              About Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Who We Are
            </h2>
            <p className="text-lg text-[#64748B] leading-relaxed mb-6">
              <span className="font-semibold text-[#0F172A]">Ezeefit Office Systems</span> is a division of{" "}
              <span className="font-semibold text-[#0F172A]">Ezeefit Modular Furniture Pvt. Ltd.</span>, 
              delivering scalable office furniture and workspace solutions for corporates across India.
            </p>
            <p className="text-[#64748B] leading-relaxed mb-8">
              Since 2014, we have been transforming workspaces with our end-to-end 
              furniture solutions — from concept and design to manufacturing and installation. Our commitment 
              to quality, backed by ISO 9001:2015 certification, has made us a trusted partner for enterprises looking 
              for reliable, scalable office infrastructure.{" "}
              <Link to="/about" className="text-[#F97316] underline hover:text-orange-600 font-medium">Read our full story</Link>.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-[#F97316]">2014</p>
                <p className="text-sm text-[#64748B]">Established</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#F97316]">ISO 9001:2015</p>
                <p className="text-sm text-[#64748B]">Certified Products</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#F97316]">Mumbai</p>
                <p className="text-sm text-[#64748B]">Own Manufacturing Facility</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Our Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A]">
              Built for Scale & Precision
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => {
              const Icon = getIcon(capability.icon, index);
              return (
                <motion.div
                  key={capability.title || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-8 border border-[#E2E8F0] card-hover bg-white"
                >
                  <div className="w-16 h-16 bg-[#F8FAFC] flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-[#F97316]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-[#64748B] leading-relaxed">
                    {capability.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-24 bg-[#F8FAFC] mfg-grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Production Flow
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A]">
              Our Manufacturing Process
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative p-8 bg-white border border-[#E2E8F0]"
              >
                <span className="process-step-number absolute top-4 right-4">
                  {step.number}
                </span>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2 relative z-10">
                  {step.title}
                </h3>
                <p className="text-[#64748B] relative z-10">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Factory Video */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Precision in Motion
            </h2>
          </motion.div>

          <FactoryVideoPlayer />
        </div>
      </section>

      <ExploreMore currentPage="manufacturing" />
    </div>
  );
}
